import React from 'react';
import { useIntl } from 'umi';
import Bubble from '@home/components/TeaComponents/Bubble';
import GameImage from '@home/components/GameImage';
import { gotoIntelligenceGame } from '@home/utils/utils';
import { getCountryFlag } from '@home/_config/countries';
import { formatKMBWithPlaceholder, formatDecimalToPercentage } from '@intelligence/v3/utils/number';
import { getGenreNameI18n } from '@intelligence/v3/utils/genre';
import StyledSubtitle from '../StyledSubtitle';
import IconSteam from '@intelligence/assets/imgs/CasualWordlWide/icon-steam.png';
import Rank01 from '@intelligence/assets/imgs/CasualWordlWide/rank01.png';
import Rank02 from '@intelligence/assets/imgs/CasualWordlWide/rank02.png';
import Rank03 from '@intelligence/assets/imgs/CasualWordlWide/rank03.png';
import './index.scss';

/** steam.top_list[] 接口字段 */
interface SteamTopListItem {
  game_id: string;
  edition_id: string;
  combine_id: string;
  game_name: string;
  cover: string;
  genre: string;
  sub_genre: string;
  publisher: string;
  publisher_country_abbre: string;
  steam__pcu_rank: number | null;
  steam__peak_pcu: number | null;
  steam__peak_pcu_growth: number | null;
  steam__peak_pcu_ratio: number | null;
  steam__pcu_rank_growth: number | null;
  release_time: string;
  is_new_online?: boolean;
  change_type?: string;
}

/** steam.anomalies[] 接口字段 */
interface SteamAnomalyItem {
  game_id: string;
  edition_id: string;
  game_name: string;
  cover: string;
  genre: string;
  sub_genre: string;
  publisher: string;
  publisher_country_abbre: string;
  country_region: string;
  change_type: string;
  release_time: string;
  summary_zh: string;
  summary_en: string;
  steam__pcu_rank: number | null;
  steam__pcu_rank_growth: number | null;
  steam__peak_pcu: number | null;
  steam__peak_pcu_growth: number | null;
}

/** steam 接口字段 */
export interface SteamData {
  top_list: SteamTopListItem[];
  anomalies: SteamAnomalyItem[];
}

interface ISteamBreakoutsProps {
  data: SteamData | null;
  reportDate?: string;
  locale?: string;
  genreOptionsMap?: IRecord<any[]>;
}

/** 计算发售天数：reportDate - release_time */
const calcReleaseDays = (releaseTime: string, reportDate?: string): string => {
  if (!releaseTime) return '-';
  const releaseDate = new Date(releaseTime);
  const baseDate = reportDate ? new Date(reportDate) : new Date();
  const diffMs = baseDate.getTime() - releaseDate.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays < 0) return '-';
  return `${diffDays}`;
};

const SteamBreakouts: React.FC<ISteamBreakoutsProps> = ({ data, reportDate, locale, genreOptionsMap }) => {
  const { formatMessage } = useIntl();
  const isZh = locale === 'zh-CN';

  if (!data) {
    return null;
  }

  const { top_list = [], anomalies = [] } = data;

  const translateGenre = (genreValue: string) =>
    genreValue.split('|').map((v) => getGenreNameI18n({ locale, genreOptionsMap: genreOptionsMap || {}, genreSource: 'iegg', genreValue: v.trim() })).join('|');

  const handleGoToGameDetails = (gameId: string) => {
    if (gameId) {
      gotoIntelligenceGame(gameId, 'combine', 'stats');
    }
  };

  const renderTags = (genre: string, subGenre: string) => {
    const allTags = `${genre}|${subGenre}`.split('|').filter(Boolean);
    const uniqueTags = Array.from(new Set(allTags.map((v) => v.trim())));

    return uniqueTags.map((tag, idx) => {
      const name = getGenreNameI18n({ locale, genreOptionsMap: genreOptionsMap || {}, genreSource: 'iegg', genreValue: tag });
      return (
        <Bubble content={name} key={idx}>
          <div className={`dbi-worldwide-game-tag ${idx === 0 ? '--first' : ''}`}>{name}</div>
        </Bubble>
      );
    });
  };

  // 渲染游戏卡片（异动用）
  const renderGameCard = (game: { edition_id: string; cover: string; game_name: string; publisher: string; publisher_country_abbre: string; genre: string; sub_genre: string }) => (
    <div className="dbi-worldwide-game-card">
      <div className="dbi-worldwide-game-icon">
        <GameImage entityType="pc" src={game.cover} alt={game.game_name} />
      </div>
      <div className="dbi-worldwide-game-info">
        <div className="dbi-worldwide-game-name">
          <Bubble content={game.game_name}>
            <span>{game.game_name}</span>
          </Bubble>
        </div>
        <div className="dbi-worldwide-game-country">
          {game.publisher_country_abbre && (
            <img className="dbi-worldwide-game-country-img" src={getCountryFlag(game.publisher_country_abbre)} alt={game.publisher_country_abbre} />
          )}
          <Bubble content={game.publisher}>
            <span className="dbi-worldwide-game-country-text">{game.publisher}</span>
          </Bubble>
        </div>
        <div className="dbi-worldwide-game-tags dbi-worldwide-game-tags--blue">
          {renderTags(game.genre, game.sub_genre)}
        </div>
      </div>
    </div>
  );

  // 渲染排名表格
  const renderRankingTable = () => (
    <div className="dbi-worldwide-panel dbi-worldwide-hot-games">
      <div className="dbi-worldwide-ranking__table">
        <div className="dbi-worldwide-ranking__table-header">
          <div className="dbi-worldwide-ranking__table-th">
            <div className="dbi-worldwide-ranking__table-th-title">
              <span><img src={IconSteam} alt="" /></span>
              <span>{formatMessage({ id: 'dbi_worldwide_steam_hot_games' })}</span>
            </div>
          </div>
          <div className="dbi-worldwide-ranking__table-th">
            <div className="s1">
              {formatMessage({ id: 'dbi_worldwide_steam_pcu_24h' })}
              <span className="dbi-worldwide-rank-arrow"></span>
            </div>
          </div>
          <div className="dbi-worldwide-ranking__table-th">{formatMessage({ id: 'dbi_worldwide_steam_change' })}</div>
          <div className="dbi-worldwide-ranking__table-th">{formatMessage({ id: 'dbi_worldwide_steam_release_days' })}</div>
          <div className="dbi-worldwide-ranking__table-th">{formatMessage({ id: 'dbi_worldwide_iegg_genre' })}</div>
        </div>
        <div className="dbi-worldwide-ranking__table-tbody">
          {top_list.map((item, idx) => {
            const pcuGrowth = item.steam__peak_pcu_growth;
            const isNewOnline = !!item.is_new_online;
            const pcuTrendType = pcuGrowth === null || pcuGrowth === 0 ? 'equal' : pcuGrowth > 0 ? 'up' : 'down';
            const pcuTrendText = formatDecimalToPercentage(item.steam__peak_pcu_ratio, { emptyValue: '' });

            return (
              <div className="dbi-worldwide-ranking__table-row" key={item.game_id || idx} onClick={() => handleGoToGameDetails(item.combine_id)} style={{ cursor: 'pointer' }}>
                <div className="dbi-worldwide-ranking__table-td">
                  <div className="dbi-worldwide-ranking-figure">
                    <div className="dbi-worldwide-ranking-index">
                      {(item.steam__pcu_rank ?? idx + 1) <= 3
                        ? <img src={(item.steam__pcu_rank ?? idx + 1) === 1 ? Rank01 : (item.steam__pcu_rank ?? idx + 1) === 2 ? Rank02 : Rank03} alt="" className="dbi-worldwide-ranking-index-img" />
                        : (item.steam__pcu_rank ?? idx + 1)}
                    </div>
                    <div className="dbi-worldwide-ranking-figure-content">
                      <div className="dbi-worldwide-ranking-figure-avatar">
                        <GameImage entityType="pc" src={item.cover} alt={item.game_name} />
                      </div>
                      <div className="dbi-worldwide-ranking-figure-info">
                        <div className="dbi-worldwide-ranking-figure-title">
                          <Bubble content={item.game_name}>
                            <span className="dbi-worldwide-ranking-figure-name">{item.game_name}</span>
                          </Bubble>
                          {item.change_type ? (
                            item.change_type === '突增' ? (
                              <span className="dbi-worldwide-ranking-tag --surge">{formatMessage({ id: 'dbi_worldwide_tag_surge' })}</span>
                            ) : (
                              <span className="dbi-worldwide-ranking-tag --new">{formatMessage({ id: 'dbi_worldwide_tag_new' })}</span>
                            )
                          ) : null}
                        </div>
                        <div className="dbi-worldwide-ranking-figure-desc">
                          {item.publisher_country_abbre && (
                            <img className="dbi-worldwide-ranking-figure-desc-img" src={getCountryFlag(item.publisher_country_abbre)} alt="" />
                          )}
                          <span className="dbi-worldwide-ranking-figure-desc-text">{item.publisher}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="dbi-worldwide-ranking__table-td">
                  <div className="dbi-worldwide-trend-text">
                    <div className="dbi-worldwide-trend-nums">{formatKMBWithPlaceholder(item.steam__peak_pcu)}</div>
                    {isNewOnline ? (
                      <span className="dbi-worldwide-game-income-trend --up" style={{ color: '#009444' }}>{formatMessage({ id: 'dbi_worldwide_new_entry' })}</span>
                    ) : pcuTrendType === 'equal' ? (
                      <span className="dbi-worldwide-game-income-trend --equal"></span>
                    ) : (
                      <span className={`dbi-worldwide-game-income-trend --${pcuTrendType}`}>{pcuTrendType === 'up' && pcuTrendText ? `+${pcuTrendText}` : pcuTrendText}</span>
                    )}
                  </div>
                </div>
                <div className="dbi-worldwide-ranking__table-td">
                  <div className="dbi-worldwide-trend-text">
                    <div className="dbi-worldwide-trend-nums">
                      {pcuTrendType === 'equal' ? '-' : `${pcuGrowth! > 0 ? '+' : ''}${formatKMBWithPlaceholder(pcuGrowth)}`}
                    </div>
                  </div>
                </div>
                <div className="dbi-worldwide-ranking__table-td">
                  <div className="dbi-worldwide-trend-text">
                    <div className="dbi-worldwide-trend-nums">{calcReleaseDays(item.release_time, reportDate)}{calcReleaseDays(item.release_time, reportDate) !== '-' ? ` ${formatMessage({ id: 'dbi_worldwide_days_unit' })}` : ''}</div>
                  </div>
                </div>
                <div className="dbi-worldwide-ranking__table-td">
                  <div className="dbi-worldwide-trend-category">
                    {(item.genre || item.sub_genre) ? (
                      <div className="dbi-worldwide-game-tags dbi-worldwide-game-tags--blue">
                        {renderTags(item.genre, item.sub_genre)}
                      </div>
                    ) : (
                      <span className="dbi-worldwide-trend-nums" style={{ textAlign: 'right', display: 'block' }}>TBD</span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );

  return (
    <div className="dbi-worldwide-tabcontent">
      <div className="dbi-worldwide-title-wrap">
        <StyledSubtitle text={formatMessage({ id: 'dbi_worldwide_steam_breakouts' })} />
        <div className="dbi-worldwide-tips">
          <div className="dbi-worldwide-tips-item">
            <b>{formatMessage({ id: 'dbi_worldwide_mobile_tip_new_label' })}</b>
            {formatMessage({ id: 'dbi_worldwide_steam_tip_new_desc' })}
          </div>
          <div className="dbi-worldwide-tips-item">
            <b>{formatMessage({ id: 'dbi_worldwide_mobile_tip_surge_label' })}</b>
            {formatMessage({ id: 'dbi_worldwide_steam_tip_surge_desc' })}
          </div>
        </div>
      </div>

      {/* 异动分析 */}
      {anomalies.length > 0 && (
        <div className="dbi-worldwide-panel dbi-worldwide-anomaly-steam-panel">
          <div className="dbi-worldwide-panel-wrap">
            <div className="dbi-worldwide-panel-header">
              <div className="dbi-worldwide-header-left">
                <span className="dbi-worldwide-steam-icon">
                  <img src={IconSteam} alt="" />
                </span>
                <span className="dbi-worldwide-panel-title">{formatMessage({ id: 'dbi_worldwide_steam_anomaly_title' })}</span>
              </div>
              <div className="dbi-worldwide-header-right">
                <span className="dbi-worldwide-tip">{formatMessage({ id: 'dbi_worldwide_steam_pcu_source' })}</span>
              </div>
            </div>
            <div className="dbi-worldwide-anomaly-list dbi-worldwide-anomaly-list-steam">
              {anomalies.map((item, idx) => (
                <div className="dbi-worldwide-anomaly-item" key={item.game_id || idx} onClick={() => handleGoToGameDetails(item.combine_id)} style={{ cursor: 'pointer' }}>
                  <div className={`dbi-worldwide-anomaly-badge --${item.change_type === '新上线' ? 'new' : 'increase'}`}>
                    {item.change_type === '新上线' ? formatMessage({ id: 'dbi_worldwide_mobile_tip_new_label' }) : formatMessage({ id: 'dbi_worldwide_mobile_tip_surge_label' })}
                  </div>
                  {renderGameCard(item)}
                  <div className="dbi-worldwide-anomaly-intro">
                    {item.release_time && (
                      <div className="dbi-worldwide-anomaly-intro-top">
                        <div className="dbi-worldwide-anomaly-income">
                          <img src={IconSteam} alt="" />
                          <span className="dbi-worldwide-anomaly-income-title">PCU</span>
                          <span className="dbi-worldwide-anomaly-pcu-data">
                            <span className="dbi-worldwide-anomaly-pcu-value">{formatKMBWithPlaceholder(item.steam__peak_pcu)}</span>
                            {item.steam__peak_pcu_growth !== null && item.steam__peak_pcu_growth !== 0 && (
                              <span className={`dbi-worldwide-game-income-trend --${item.steam__peak_pcu_growth > 0 ? 'up' : 'down'}`}>
                                {item.steam__peak_pcu_growth > 0 ? '+' : ''}{formatKMBWithPlaceholder(item.steam__peak_pcu_growth)}
                              </span>
                            )}
                          </span>
                          <span className="dbi-worldwide-anomaly-rank-data">
                            <span className="dbi-worldwide-anomaly-rank-value">{item.steam__pcu_rank !== null ? `#${item.steam__pcu_rank}` : '-'}</span>
                            {item.steam__pcu_rank_growth !== null && item.steam__pcu_rank_growth !== 0 && (
                              <span className={`dbi-worldwide-game-income-trend --${item.steam__pcu_rank_growth > 0 ? 'up' : 'down'}`}>
                                {item.steam__pcu_rank_growth > 0 ? '+' : ''}{item.steam__pcu_rank_growth}
                              </span>
                            )}
                          </span>
                        </div>
                        <div className="dbi-worldwide-anomaly-income-release">{formatMessage({ id: 'dbi_worldwide_steam_release_days' })}: {calcReleaseDays(item.release_time, reportDate)}{calcReleaseDays(item.release_time, reportDate) !== '-' ? ` ${formatMessage({ id: 'dbi_worldwide_days_unit' })}` : ''}</div>
                      </div>
                    )}
                    <div className="dbi-worldwide-anomaly-intro-text">
                      {isZh ? item.summary_zh : item.summary_en}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* 热门游戏排名 */}
      {top_list.length > 0 && renderRankingTable()}
    </div>
  );
};

export default SteamBreakouts;
